import React from 'react';
import { TTransport } from '@/types/models';
import { formatEventDate, parseDate } from '@/utils/date';
import { TRANSPORT_EVENTS_UUIDS, TRACKING_CASES } from './constants';
import { formatTransportEta } from '@/utils/formatTransportEta';

export type ContentBoxConfig = {
  title: string | ((transport_data: TTransport) => React.ReactNode);
  description?: string | ((transport_data: TTransport) => React.ReactNode);
  eventUuid?: string;
  index: number;
  selected: boolean;
  backgroundColor?: string;
};

type TranslateFn = (
  key: string,
  params?: Record<string, string | number>
) => string;

const formatLocalizedDate = (
  date: Date,
  dateLocale: string,
  options: Intl.DateTimeFormatOptions
) => date.toLocaleDateString(dateLocale, options);

const formatLocalizedTime = (date: Date, dateLocale: string) =>
  date.toLocaleTimeString(dateLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const getDriverEnRouteDescription = (
  transport_data: TTransport,
  t: TranslateFn,
  computedEta?: string | null
) => {
  const eta =
    formatTransportEta(transport_data.interval, transport_data.eta) ??
    computedEta ??
    null;

  if (eta) {
    return t('timeline.driverEnRouteWithEta', { eta });
  }

  return t('timeline.driverEnRoute');
};

export const getContentBoxes = (
  selectedCase: string | null,
  transport_data: TTransport | null,
  t: TranslateFn,
  dateLocale: string,
  options?: { computedEta?: string | null }
): Array<ContentBoxConfig> => {
  if (!transport_data || !selectedCase) return [];

  const events = transport_data.events;

  const getEventByUuid = (uuid: string) => {
    return events.find((e) => e.event.uuid === uuid);
  };

  const driverEnRouteDescription = getDriverEnRouteDescription(
    transport_data,
    t,
    options?.computedEta
  );

  const getPostponedContentBoxes = (): Array<ContentBoxConfig> => {
    const isPickupPostponed =
      Boolean(getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickupPostponed)) &&
      !getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickedUp);

    const bookingChangedStep: ContentBoxConfig = {
      title: t('timeline.bookingChanged'),
      description: () => {
        const postponedDate = isPickupPostponed
          ? transport_data.pickup_date
          : transport_data.delivery_date || transport_data.pickup_date;
        if (!postponedDate) return '';
        const date = parseDate(postponedDate.split('T')[0]);
        const formattedDate = formatLocalizedDate(date, dateLocale, {
          month: 'short',
          day: 'numeric',
        });
        return (
          <>
            {t('timeline.orderPostponedFor')}{' '}
            <span className='font-bold'>{formattedDate}.</span>
          </>
        );
      },
      index: 2,
      selected: true,
      backgroundColor: 'bg-redSecondary',
    };

    if (isPickupPostponed) {
      return [
        {
          title: t('timeline.registered'),
          description: () => {
            const registeredEvent = getEventByUuid(
              TRANSPORT_EVENTS_UUIDS.registered
            );
            if (!registeredEvent?.created_at) {
              return t('timeline.bookingRegistered');
            }
            const formattedDate = formatEventDate(
              registeredEvent.created_at,
              dateLocale
            );
            if (!formattedDate) return t('timeline.bookingRegistered');
            return (
              <>
                {t('timeline.orderRegisteredOn')}{' '}
                <span className='font-bold'>{formattedDate}</span>
              </>
            );
          },
          index: 0,
          selected: false,
        },
        {
          title: t('timeline.outForPickup'),
          description: '',
          index: 1,
          selected: false,
        },
        bookingChangedStep,
      ];
    }

    return [
      {
        title: '',
        description: () => {
          const pickedUpEvent = getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickedUp);
          if (!pickedUpEvent || !pickedUpEvent?.created_at) return '';
          const date = new Date(pickedUpEvent.created_at);
          const formattedDate = formatLocalizedDate(date, dateLocale, {
            month: 'short',
            day: 'numeric',
          });
          const time = formatLocalizedTime(date, dateLocale);
          return (
            <>
              {t('timeline.pickedUpOnAt')}{' '}
              <span className='font-bold'>
                {formattedDate} {t('timeline.at')} {time}
              </span>
            </>
          );
        },
        index: 0,
        selected: false,
      },
      {
        title: '',
        description: () => {
          const arrivalEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.arrivalAtDepot
          );
          if (!arrivalEvent || !arrivalEvent?.created_at) return '';
          const date = new Date(arrivalEvent.created_at);
          const formattedDate = formatLocalizedDate(date, dateLocale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
          const time = formatLocalizedTime(date, dateLocale);
          return (
            <>
              {t('timeline.shipmentArrivedAtDepot')}{' '}
              <span className='font-bold'>
                {formattedDate} - {time}
              </span>
            </>
          );
        },
        index: 1,
        selected: false,
      },
      bookingChangedStep,
    ];
  };

  const contentBoxesMap: Record<string, Array<ContentBoxConfig>> = {
    [TRACKING_CASES.cancelled]: [
      {
        title: '',
        description: () => {
          const registeredEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.registered
          );

          if (!registeredEvent || !registeredEvent?.created_at) return '';
          const formattedDate = formatEventDate(
            registeredEvent?.created_at,
            dateLocale
          );
          if (!formattedDate) return '';
          return (
            <>
              {t('timeline.orderRegisteredOn')}{' '}
              <span className='font-extrabold' style={{ fontWeight: 800 }}>
                {formattedDate}
              </span>
            </>
          );
        },
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.orderCancelled'),
        description: () => {
          const cancelledEvent = getEventByUuid(TRANSPORT_EVENTS_UUIDS.cancel);
          if (!cancelledEvent || !cancelledEvent?.created_at) return '';
          const formattedDate = formatEventDate(
            cancelledEvent?.created_at,
            dateLocale
          );
          if (!formattedDate) return '';
          return (
            <>
              {t('timeline.cancelledOn')}{' '}
              <span className='font-bold'>{formattedDate}</span>
            </>
          );
        },
        index: 1,
        selected: true,
        backgroundColor: 'bg-redSecondary',
      },
    ],
    [TRACKING_CASES.registered]: [
      {
        title: t('timeline.registered'),
        description: () => t('timeline.bookingRegistered'),
        index: 0,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: t('timeline.orderPickedUp'),
        description: '',
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.furtherUpdates'),
        description: '',
        index: 2,
        selected: false,
      },
    ],
    [TRACKING_CASES.outForPickup]: [
      {
        title: t('timeline.outForPickup'),
        description: driverEnRouteDescription,
        index: 0,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: t('timeline.arrivingShortly'),
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.orderPickedUp'),
        description: '',
        index: 2,
        selected: false,
      },
    ],
    [TRACKING_CASES.intervalPickup]: [
      {
        title: t('timeline.outForPickup'),
        description: driverEnRouteDescription,
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.arrivingShortly'),
        description: '',
        index: 1,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: t('timeline.orderPickedUp'),
        description: '',
        index: 2,
        selected: false,
      },
    ],
    [TRACKING_CASES.intervalDelivery]: [
      {
        title: t('timeline.outForDelivery'),
        description: driverEnRouteDescription,
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.arrivingShortly'),
        description: '',
        index: 1,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: t('timeline.orderDelivered'),
        description: '',
        index: 2,
        selected: false,
      },
    ],
    [TRACKING_CASES.pickedUp]: [
      {
        title: t('timeline.outForPickupEnRoute'),
        description: '',
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.thereShortly'),
        description: '',
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.orderPickedUpTitle'),
        description: () => {
          const pickedUpEvent = getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickedUp);
          if (!pickedUpEvent || !pickedUpEvent?.created_at) return '';
          const pickedUpDate = new Date(pickedUpEvent.created_at);
          const timeOnly = formatLocalizedTime(pickedUpDate, dateLocale);
          return (
            <>
              {t('timeline.receivedAt')}{' '}
              <span className='font-bold'>{timeOnly}</span>
            </>
          );
        },
        index: 2,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
    ],
    [TRACKING_CASES.arrivalAtDepot]: [
      {
        title: '',
        description: () => {
          const pickedUpEvent = getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickedUp);
          if (!pickedUpEvent || !pickedUpEvent?.created_at)
            return t('timeline.bookingRegistered');
          const formattedDate = formatEventDate(
            pickedUpEvent?.created_at,
            dateLocale
          );
          if (!formattedDate) return t('timeline.bookingRegistered');
          return (
            <>
              {t('timeline.pickedUpOn')}{' '}
              <span className='font-bold'>{formattedDate}</span>
            </>
          );
        },
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.arrivedAtDepot'),
        description: () => {
          const arrivalEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.arrivalAtDepot
          );
          if (!arrivalEvent || !arrivalEvent?.created_at) return '';
          const formattedDate = formatEventDate(
            arrivalEvent?.created_at,
            dateLocale
          );
          if (!formattedDate) return '';
          return (
            <>
              {t('timeline.arrivedAtDepotDesc')}{' '}
              <span className='font-bold'>{formattedDate}</span>
            </>
          );
        },
        index: 1,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: '',
        description: t('timeline.furtherUpdates'),
        index: 2,
        selected: false,
      },
    ],
    [TRACKING_CASES.departureFromDepot]: [
      {
        title: '',
        description: () => {
          const pickedUpEvent = getEventByUuid(TRANSPORT_EVENTS_UUIDS.pickedUp);
          if (!pickedUpEvent || !pickedUpEvent?.created_at) return '';
          const date = new Date(pickedUpEvent.created_at);
          const formattedDate = formatLocalizedDate(date, dateLocale, {
            month: 'short',
            day: 'numeric',
          });
          const time = formatLocalizedTime(date, dateLocale);
          return (
            <>
              {t('timeline.pickedUpOnAt')}{' '}
              <span className='font-bold'>
                {formattedDate} {t('timeline.at')} {time}
              </span>
            </>
          );
        },
        index: 0,
        selected: false,
      },
      {
        title: '',
        description: () => {
          const arrivalEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.arrivalAtDepot
          );
          if (!arrivalEvent || !arrivalEvent?.created_at) return '';
          const date = new Date(arrivalEvent.created_at);
          const formattedDate = formatLocalizedDate(date, dateLocale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
          const time = formatLocalizedTime(date, dateLocale);
          return (
            <>
              {t('timeline.shipmentArrivedAtDepot')}{' '}
              <span className='font-bold'>
                {formattedDate} - {time}
              </span>
            </>
          );
        },
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.departedDepot'),
        description: () => {
          const departureEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.departureFromDepot
          );
          if (!departureEvent || !departureEvent?.created_at) return '';
          const date = new Date(departureEvent.created_at);
          const formattedDate = formatLocalizedDate(date, dateLocale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
          const time = formatLocalizedTime(date, dateLocale);
          return (
            <>
              {t('timeline.departedDepotDesc')}{' '}
              <span className='font-bold'>
                {formattedDate} - {time}
              </span>
            </>
          );
        },
        index: 2,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
    ],
    [TRACKING_CASES.delivered]: [
      {
        title: t('timeline.outForDelivery'),
        description: driverEnRouteDescription,
        index: 0,
        selected: false,
      },
      {
        title: t('timeline.arrivingShortly'),
        description: '',
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.delivered'),
        description: () => {
          const deliveredEvent = getEventByUuid(
            TRANSPORT_EVENTS_UUIDS.delivered
          );
          if (!deliveredEvent || !deliveredEvent?.created_at) return '';
          const dateObj = new Date(deliveredEvent.created_at);
          const formattedTime = dateObj.toLocaleTimeString(dateLocale, {
            hour: '2-digit',
            minute: '2-digit',
          });
          return (
            <>
              {t('timeline.deliveredAt')}{' '}
              <span className='font-bold'>{formattedTime}</span>
            </>
          );
        },
        index: 2,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
    ],
    [TRACKING_CASES.outForDelivery]: [
      {
        title: t('timeline.outForDelivery'),
        description: driverEnRouteDescription,
        index: 0,
        selected: true,
        backgroundColor: 'bg-greenSecondary',
      },
      {
        title: t('timeline.arrivingShortly'),
        description: '',
        index: 1,
        selected: false,
      },
      {
        title: t('timeline.orderDelivered'),
        description: '',
        index: 2,
        selected: false,
      },
    ],
  };

  if (selectedCase === TRACKING_CASES.postponed) {
    return getPostponedContentBoxes();
  }

  return contentBoxesMap[selectedCase] || [];
};
